import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { glassTextField, glassSelect, getStaggeredAnimation } from './glassMorphismStyles';
import GlassSectorSelect from './GlassSectorSelect';

export interface SharedFormData {
  platform: string;
  brand: string;
  brandDescription?: string;
  sector: string;
  sectorId?: string;
}

interface SharedFormFieldsProps {
  formData: SharedFormData;
  onChange: (field: string, value: string) => void;
  errors?: Record<string, string>;
  animationBaseDelay?: number;
}

const platforms = [
  { value: 'Twitter/X', label: 'Twitter/X' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'TikTok', label: 'TikTok' },
];

const SharedFormFields: React.FC<SharedFormFieldsProps> = ({
  formData,
  onChange,
  errors = {},
  animationBaseDelay = 0,
}) => {
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.name, event.target.value);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    onChange('platform', event.target.value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 } }}>
      {/* Platform Dropdown */}
      <FormControl 
        fullWidth 
        size="small"
        error={!!errors.platform}
        sx={{
          ...glassSelect,
          ...getStaggeredAnimation(0, animationBaseDelay),
        }}
      >
        <InputLabel id="platform-label">Platform *</InputLabel>
        <Select
          labelId="platform-label"
          id="platform"
          name="platform"
          value={formData.platform}
          label="Platform *"
          onChange={handleSelectChange}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: 'rgba(0, 51, 78, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(10, 124, 160, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                '& .MuiList-root': {
                  padding: 0,
                },
                '& .MuiMenuItem-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px 16px',
                  borderBottom: '1px solid rgba(10, 124, 160, 0.1)',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(10, 124, 160, 0.2)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(10, 124, 160, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(10, 124, 160, 0.4)',
                    },
                  },
                },
              },
            },
          }}
        >
          {platforms.map((platform) => (
            <MenuItem 
              key={platform.value} 
              value={platform.value}
            >
              {platform.label}
            </MenuItem>
          ))}
        </Select>
        {errors.platform && (
          <Box 
            component="span" 
            sx={{ 
              color: '#ff6b6b', 
              fontSize: '0.75rem', 
              mt: 0.5,
              ml: 1.5,
            }}
          >
            {errors.platform}
          </Box>
        )}
      </FormControl>

      {/* Brand/Sender Field */}
      <TextField
        fullWidth
        size="small"
        name="brand"
        label="Brand/Sender"
        placeholder="Your brand or company name"
        value={formData.brand}
        onChange={handleTextChange}
        error={!!errors.brand}
        helperText={errors.brand}
        required
        sx={{
          ...glassTextField,
          ...getStaggeredAnimation(1, animationBaseDelay),
        }}
      />

      {/* Brand Description Field */}
      <TextField
        fullWidth
        size="small"
        name="brandDescription"
        label="Brand Description"
        placeholder="Brief brand description to help us generate more accurate content"
        value={formData.brandDescription || ''}
        onChange={handleTextChange}
        multiline
        minRows={1}
        maxRows={2}
        inputProps={{ maxLength: 500 }}
        sx={{
          ...glassTextField,
          ...getStaggeredAnimation(2, animationBaseDelay),
          '& .MuiFormHelperText-root': {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.7rem',
          },
        }}
      />

      {/* Sector Field */}
      <GlassSectorSelect
        value={formData.sector}
        onChange={(sectorName, sectorObj) => {
          onChange('sector', sectorName);
          // Also pass the sector object if available for the ID
          if (sectorObj) {
            onChange('sectorId', sectorObj.id);
          }
        }}
        error={errors.sector}
        animationDelay={animationBaseDelay + 300}
      />
    </Box>
  );
};

export default SharedFormFields;
